import * as React from "react";
import { useEffect } from "react";
import ProgressBar from "@ramonak/react-progress-bar";
import SpreetailHeader from "../layout/SpreetailHeader";

interface ProgressSheetProps {
  isOpen: boolean;
  progress: number;
  title: string;
  subtitle?: string;
  stepMessage?: string;
}

const ProgressSheet: React.FC<ProgressSheetProps> = ({
  isOpen,
  progress,
  title,
  subtitle,
  stepMessage,
}) => {
  // Lock body scroll when sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          @keyframes progressSheetFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes progressSpinnerRotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .progress-sheet-bg-gradient {
            background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
          }
        `}
      </style>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="progress-sheet-title"
        className="progress-sheet-bg-gradient"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          fontFamily: "inherit",
          animation: "progressSheetFadeIn 0.2s ease-out forwards",
        }}
      >
        {/* Decorative blur circles */}
        <div
          style={{
            position: "absolute",
            right: -40,
            top: -40,
            width: 200,
            height: 200,
            filter: "blur(40px)",
            pointerEvents: "none",
            background: "radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: -32,
            bottom: -32,
            width: 152,
            height: 152,
            filter: "blur(40px)",
            pointerEvents: "none",
            background: "radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)",
          }}
        />

        {/* Header */}
        <SpreetailHeader />

        {/* Centered content */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "320px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Circular Spinner - Simple teal ring */}
            <div
              style={{
                width: 52,
                height: 52,
                marginBottom: 24,
              }}
            >
              <div
                style={{
                  width: 50,
                  height: 50,
                  borderRadius: "50%",
                  border: "3px solid rgba(6, 142, 117, 0.2)",
                  borderTopColor: "#068E75",
                  animation: "progressSpinnerRotate 1s linear infinite",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Title */}
            <h2
              id="progress-sheet-title"
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#1F2937",
                textAlign: "center",
                margin: 0,
                marginBottom: 8,
              }}
            >
              {title}
            </h2>

            {/* Subtitle */}
            {subtitle && (
              <p
                style={{
                  fontSize: "13px",
                  color: "#6B7280",
                  textAlign: "center",
                  margin: 0,
                  marginBottom: 24,
                  lineHeight: 1.5,
                }}
              >
                {subtitle}
              </p>
            )}

            {/* Progress bar */}
            <div style={{ width: "100%", marginBottom: 8 }}>
              <ProgressBar
                height="12px"
                bgColor="#068E75"
                labelSize="0px"
                completed={Math.round(progress)}
                maxCompleted={100}
                width="100%"
                borderRadius="6px"
                baseBgColor="rgba(255, 255, 255, 0.8)"
              />
            </div>

            {/* Percentage */}
            <p
              style={{
                fontSize: "13px",
                color: "#068E75",
                textAlign: "center",
                margin: 0,
                marginBottom: stepMessage ? 12 : 0,
                fontWeight: 600,
              }}
            >
              {Math.round(progress)}%
            </p>

            {/* Step message (optional - for Market Watch and Analysis) */}
            {stepMessage && (
              <p
                style={{
                  fontSize: "12px",
                  color: "#065F46",
                  textAlign: "center",
                  margin: 0,
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                {stepMessage}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProgressSheet;
